import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ConfigProvider, Form, Input, Upload } from 'antd';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { InboxOutlined } from '@ant-design/icons';
import useAxiosClient from '../axios-client.js';
import { useStateContext } from '../contexts/ContextProvider.jsx';
import { PRIMARY, SHELL_THEME as THEME, shellAntdTheme, STATUS, hexToRgba } from '../shellTokens';
import { addressLines, contactLines, logoSrc } from '../utils/company';
import '../components/Util/formControls.css';

const { Dragger } = Upload;
const { TextArea } = Input;

const MAX_LOGO_BYTES = 2 * 1024 * 1024; // matches the API's max:2048 (KB)
const LOGO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/** The fields the form owns, in payload shape. Anything else is read-only. */
const FIELDS = [
  'name', 'legalName', 'tagline',
  'phone', 'altPhone', 'email', 'website',
  'addressLine1', 'addressLine2', 'city', 'postcode', 'country',
  'bin', 'tin', 'invoiceTerms', 'invoiceFooter',
];

const pickFields = (company) =>
  FIELDS.reduce((acc, f) => ({ ...acc, [f]: company?.[f] ?? '' }), {});

const Section = ({ title, hint, children }) => (
  <div style={{
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 14, padding: '16px 18px 18px',
  }}>
    <div style={{ marginBottom: 14 }}>
      <div style={{ font: '700 14px Outfit, sans-serif' }}>{title}</div>
      {hint && (
        <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
          {hint}
        </div>
      )}
    </div>
    {children}
  </div>
);

const CompanySettings = () => {
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const { darkMode, permissions, setCompany: publish } = useStateContext();
  const [form] = Form.useForm();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Reads are open to any signed-in user because every invoice needs them; only
  // writes carry COMPANY_SETTINGS. So the screen is readable by all and simply
  // renders locked for anyone who cannot edit, rather than 404ing at them.
  const canEdit = (permissions || []).includes('COMPANY_SETTINGS');

  const rootStyle = useMemo(() => ({
    ...Object.fromEntries(
      Object.entries(THEME[darkMode ? 'dark' : 'light']).map(([k, v]) => [`--${k}`, v])
    ),
    '--primary': PRIMARY,
    // Read by the shared focus ring in formControls.css, so it follows the
    // user's accent instead of the order form's hardcoded amber.
    '--focusRing': hexToRgba(PRIMARY, 0.12),
  }), [darkMode]);

  // Every change is published to the shell's copy too, so an invoice printed
  // right after a save prints the new letterhead without a reload.
  const applyCompany = useCallback((data) => {
    setCompany(data);
    publish(data);
  }, [publish]);

  const fetchCompany = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get('/company');
      const data = res.data.data;
      applyCompany(data);
      form.setFieldsValue(pickFields(data));
      setDirty(false);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [axiosClient, form, applyCompany]);

  useEffect(() => { fetchCompany(); }, [fetchCompany]);

  const onFinish = async (values) => {
    setSaving(true);
    try {
      const res = await axiosClient.put('/company', values);
      applyCompany(res.data.data);
      form.setFieldsValue(pickFields(res.data.data));
      setDirty(false);
      toast.success(res.data.message || 'Company settings updated.');
    } catch (error) {
      const errors = error.response?.data?.errors;
      if (errors) {
        // Surface the API's own field messages on the fields themselves rather
        // than in a toast that vanishes before the user finds the input.
        form.setFields(
          Object.entries(errors).map(([name, messages]) => ({ name, errors: messages }))
        );
      }
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async ({ file, onSuccess, onError }) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('logo', file);
    try {
      // Posted through axiosClient, not Upload's own `action`: this endpoint is
      // behind COMPANY_SETTINGS and a bare browser upload carries no token.
      const res = await axiosClient.post('/company/logo', formData);
      applyCompany(res.data.data);
      onSuccess?.(res.data);
      toast.success(res.data.message || 'Logo updated.');
    } catch (error) {
      onError?.(error);
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    setUploading(true);
    try {
      const res = await axiosClient.delete('/company/logo');
      applyCompany(res.data.data);
      toast.success(res.data.message || 'Logo removed.');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setUploading(false);
    }
  };

  const beforeUpload = (file) => {
    if (!LOGO_TYPES.includes(file.type)) {
      toast.error('The logo must be a JPG, PNG or WebP image.');
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('The logo must be smaller than 2MB.');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const src = logoSrc(company);
  const preview = Form.useWatch([], form) || {};

  return (
    <div className="et-company et-controls" style={rootStyle}>
      <style>{`
        .et-company{font-family:Outfit,system-ui,sans-serif;color:var(--text)}
        .et-company .ant-form-item{margin-bottom:0}
        .et-company .ant-form-vertical .ant-form-item-label{padding:0 0 6px;line-height:1.2}
        .et-company .ant-form-item-label>label{height:auto;font-weight:600;font-size:11.5px;color:var(--muted)}
        .et-company__body{display:flex;gap:16px;align-items:flex-start;margin-top:16px}
        .et-company__main{flex:1;min-width:0;display:flex;flex-direction:column;gap:16px}
        .et-company__rail{flex:none;width:320px;display:flex;flex-direction:column;gap:16px}
        .et-company__grid2{display:grid;grid-template-columns:1fr 1fr;gap:16px 18px}
        .et-company__btnGhost{height:38px;padding:0 14px;border-radius:9px;border:1px solid var(--border);background:var(--surface);color:var(--muted);font:600 13px Outfit,sans-serif;cursor:pointer}
        .et-company__btnGhost:hover{background:var(--hover);color:var(--text)}
        .et-company__btnGhost:disabled{opacity:.45;cursor:default}
        @media (max-width:900px){
          .et-company__body{flex-direction:column}
          .et-company__rail{width:100%}
        }
        @media (max-width:640px){
          .et-company__grid2{grid-template-columns:1fr}
        }
      `}</style>

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        <Form
          form={form}
          layout="vertical"
          disabled={!canEdit || loading || saving}
          onFinish={onFinish}
          onValuesChange={() => setDirty(true)}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--dim)' }}>
                <span style={{ cursor: 'pointer' }} onClick={() => navigate('/settings')}>Settings</span>{' '}
                <span style={{ color: 'var(--borderStrong)' }}>/</span> Company
              </div>
              <div style={{ font: '700 22px Outfit, sans-serif', letterSpacing: '-.01em', marginTop: 1 }}>
                Company Settings
              </div>
              <div style={{ font: '400 13px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
                Who you are on an invoice: the name, logo and contact details every printed document carries.
              </div>
            </div>
            {canEdit && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  type="button"
                  className="et-company__btnGhost"
                  disabled={!dirty || saving}
                  onClick={() => { form.setFieldsValue(pickFields(company)); setDirty(false); }}
                >
                  Discard
                </button>
                <Button type="primary" htmlType="submit" loading={saving} disabled={!dirty || uploading}>
                  Save changes
                </Button>
              </div>
            )}
          </div>

          {!canEdit && !loading && (
            <div style={{
              marginTop: 16, border: '1px solid var(--border)', background: 'var(--surface2)',
              borderRadius: 11, padding: '11px 14px', font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)',
            }}>
              You can see the company profile but not change it. Editing needs the
              <span style={{ color: 'var(--text)', fontWeight: 600 }}> COMPANY_SETTINGS </span>
              permission.
            </div>
          )}

          <div className="et-company__body">
            <div className="et-company__main">

              <Section title="Identity" hint="The name printed at the top of every invoice.">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div className="et-company__grid2">
                    <Form.Item
                      name="name"
                      label="Company name"
                      rules={[{ required: true, message: 'The company needs a name.' }]}
                    >
                      <Input placeholder="Elegant Tex" />
                    </Form.Item>
                    <Form.Item name="legalName" label="Registered name">
                      <Input placeholder="Only if it differs from the trading name" />
                    </Form.Item>
                  </div>
                  <Form.Item name="tagline" label="Tagline">
                    <Input placeholder="Raw Fabric Store · Wholesale Supply" />
                  </Form.Item>
                </div>
              </Section>

              <Section title="Contact">
                <div className="et-company__grid2">
                  <Form.Item name="phone" label="Phone">
                    <Input placeholder="+8801896224057" />
                  </Form.Item>
                  <Form.Item name="altPhone" label="Alternate phone">
                    <Input placeholder="Optional" />
                  </Form.Item>
                  <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'That is not a valid email address.' }]}>
                    <Input placeholder="bd.eleganttex@gmail.com" />
                  </Form.Item>
                  <Form.Item name="website" label="Website" rules={[{ type: 'url', message: 'Include the https:// prefix.' }]}>
                    <Input placeholder="https://…" />
                  </Form.Item>
                </div>
              </Section>

              <Section title="Address">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div className="et-company__grid2">
                    <Form.Item name="addressLine1" label="Address line 1">
                      <Input placeholder="House 52, Lane 2" />
                    </Form.Item>
                    <Form.Item name="addressLine2" label="Address line 2">
                      <Input placeholder="Block C, Ave No. 5" />
                    </Form.Item>
                    <Form.Item name="city" label="City">
                      <Input placeholder="Dhaka" />
                    </Form.Item>
                    <Form.Item name="postcode" label="Postcode">
                      <Input placeholder="1216" />
                    </Form.Item>
                  </div>
                  <Form.Item name="country" label="Country">
                    <Input placeholder="Bangladesh" />
                  </Form.Item>
                </div>
              </Section>

              <Section title="Invoice" hint="Printed below the goods, on the documents buyers keep.">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div className="et-company__grid2">
                    <Form.Item name="bin" label="BIN">
                      <Input placeholder="Business identification number" />
                    </Form.Item>
                    <Form.Item name="tin" label="TIN">
                      <Input placeholder="Taxpayer identification number" />
                    </Form.Item>
                  </div>
                  <Form.Item name="invoiceTerms" label="Terms">
                    <TextArea rows={4} maxLength={2000} showCount placeholder="Claims on shade or weight must be raised within seven days of receipt…" />
                  </Form.Item>
                  <Form.Item name="invoiceFooter" label="Footer line">
                    <Input placeholder="Computer generated invoice, valid without seal." />
                  </Form.Item>
                </div>
              </Section>
            </div>

            {/* Rail: the logo, and what the letterhead will say */}
            <div className="et-company__rail">
              <Section title="Logo" hint="JPG, PNG or WebP, up to 2MB.">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{
                    height: 116, borderRadius: 12, background: 'var(--surface2)',
                    border: '1px solid var(--border)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                  }}>
                    {src ? (
                      <img src={src} alt="Company logo" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                    ) : (
                      <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--dim)' }}>
                        {loading ? 'Loading…' : 'No logo set'}
                      </span>
                    )}
                  </div>

                  {canEdit && (
                    <>
                      <Dragger
                        name="logo"
                        accept="image/jpeg,image/png,image/webp"
                        multiple={false}
                        maxCount={1}
                        showUploadList={false}
                        disabled={uploading || saving}
                        beforeUpload={beforeUpload}
                        customRequest={uploadLogo}
                      >
                        <p style={{ margin: 0, color: 'var(--dim)', fontSize: 20 }}><InboxOutlined /></p>
                        <p style={{ margin: 0, font: '600 12.5px Outfit, sans-serif', color: 'var(--text)' }}>
                          {uploading ? 'Uploading…' : src ? 'Replace logo' : 'Upload a logo'}
                        </p>
                      </Dragger>

                      {src && (
                        <button
                          type="button"
                          className="et-company__btnGhost"
                          disabled={uploading}
                          onClick={removeLogo}
                          style={{ color: STATUS.errorSoft }}
                        >
                          Remove logo
                        </button>
                      )}
                    </>
                  )}
                </div>
              </Section>

              {/* Not the invoice itself — just the block it prints, so a wrong
                  phone number is visible here instead of on a customer's copy. */}
              <Section title="Letterhead preview">
                <div style={{
                  background: '#ffffff', color: '#1a1a1a', borderRadius: 10,
                  border: '1px solid var(--border)', padding: '14px 15px',
                  display: 'flex', gap: 11, alignItems: 'flex-start',
                }}>
                  {src && (
                    <img src={src} alt="" style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 6, flexShrink: 0 }} />
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ font: '700 13px Outfit, sans-serif', letterSpacing: '.4px' }}>
                      {preview.name || company?.name || '—'}
                    </div>
                    {preview.tagline && (
                      <div style={{ font: '400 9px Outfit, sans-serif', color: '#8a8a8a', marginTop: 2, letterSpacing: '.6px', textTransform: 'uppercase' }}>
                        {preview.tagline}
                      </div>
                    )}
                    {[...addressLines(preview), ...contactLines(preview)].map((line, i) => (
                      <div key={i} style={{ font: '400 9.5px Outfit, sans-serif', color: '#6b6b6b', marginTop: 2 }}>{line}</div>
                    ))}
                  </div>
                </div>
              </Section>
            </div>
          </div>
        </Form>
      </ConfigProvider>
    </div>
  );
};

export default CompanySettings;
